import { useQuery } from '@tanstack/react-query';
import { AmountLineItem, Badge } from 'corso-admin-ui';
import { Key, PropsWithChildren } from 'react';
import api from '~/api';
import { useCurrency } from '~/hooks/useCurrency';
import { useReturnSettings } from '~/hooks/useReturnSettings';
import { useStoreId } from '~/providers/StoreUserProvider';
import { formatter } from 'corso-web-lib';

function useGiftCardVariant() {
  const storeId = useStoreId();

  const { data: returnSettings } = useReturnSettings();

  const idFromPlatform =
    returnSettings?.giftCards.giftCardProductIdFromPlatform;

  return useQuery({
    queryKey: ['giftCardProduct', storeId, idFromPlatform],
    enabled: !!idFromPlatform,
    queryFn: () => {
      if (!storeId || !idFromPlatform) return null;
      return api.store(storeId).product.search({
        searchTerm: idFromPlatform,
        take: 1,
      });
    },
    select: (data) =>
      data?.find((product) => product.idFromPlatform === idFromPlatform) ??
      null,
  });
}

export type GiftCard = {
  id: Key;
  amount: number;
  incentiveAmount?: number;
  platformAdminUrl?: string;
  orderNameFromPlatform?: string;
  wasResolvedExternally: boolean;
};

export function GiftCardLineItem({
  giftCard: { amount, incentiveAmount, wasResolvedExternally },
  children,
}: PropsWithChildren<{
  giftCard: GiftCard;
}>) {
  const { currencyCode } = useCurrency();
  const { data: giftCardVariant } = useGiftCardVariant();

  return (
    <AmountLineItem
      size="sm"
      media={
        giftCardVariant?.imgUrl
          ? { src: giftCardVariant.imgUrl }
          : { icon: 'giftCard' }
      }
      title={giftCardVariant?.title ?? 'Gift Card'}
      currencyCode={currencyCode}
      amount={amount}
    >
      <AmountLineItem.Detail>
        {wasResolvedExternally && <Badge>Resolved Outside Corso</Badge>}
        {!!incentiveAmount && (
          <Badge>
            {formatter.currency(incentiveAmount, currencyCode)} Gift Card
            Incentive
          </Badge>
        )}
      </AmountLineItem.Detail>
      {children}
    </AmountLineItem>
  );
}
